/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav P�ibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Pass from "App/render/webgl/passes/pass";
import OpaqueDepth from "App/render/webgl/passes/opaque_depth";
import FullScreenQuad from "App/render/webgl/utils/full_screen_quad";

import WeightedAverage from "App/render/webgl/shaders/wavg/weighted_average";
import View from "App/view/view";
import Factory from "App/render/webgl/passes/factory";
import ColorAccumulation from "App/render/webgl/passes/wavg/color_accumulation";
import DepthAccumulation from "App/render/webgl/passes/wavg/depth_accumulation";
import {WeightedAverageTransparencyPassTypeName} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class WeightedAverageTransparency extends Pass {

    public static TypeName: string = WeightedAverageTransparencyPassTypeName;
    private fullscreenVboID: WebGLBuffer;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this.fullscreenVboID = null;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): WeightedAverageTransparency {
        return new WeightedAverageTransparency(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        if (!this.fullscreenVboID) {
            this.fullscreenVboID = FullScreenQuad.create(this._context);
        }
    }

    public release() {
        if (this.fullscreenVboID) {
            let gl = this._context.getWebGLRenderingContext();
            gl.deleteBuffer(this.fullscreenVboID);
            this.fullscreenVboID = null;
        }
    }

    public _blendToOutputBuffer(aView: View, aOpaqueDepth, aColorAccBuffer, aDepthAccBuffer, aOutputBuffer) {
        this.view = aView;

        let canvas = this._context.getCanvas();
        let contentSize = canvas.getContentSize();
        let gl = this._context.getWebGLRenderingContext();

        // we need the blend func separate to set the destination aplha premultiplier to one as the destination alpha
        // has no sence in normal opengl
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.ONE);

        aOutputBuffer.bind();

        let shaders = this._renderGraph.getShaders();
        this._activeShader = shaders.get(WeightedAverage.TypeName);
        this._activeShaderRenderable = this._activeShader.get(0);
        this._activeShaderRenderable.render();

        this.state.resetRenderablesFilters();

        let uniformLocations = this._activeShaderRenderable.getUniformLocations();

        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, aColorAccBuffer.renderable.getColorTexture());
        gl.uniform1i(uniformLocations.u_tex_acc_color, 3);

        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, aDepthAccBuffer.renderable.getColorTexture());
        gl.uniform1i(uniformLocations.u_tex_acc_depth, 2);

        // we have to render the fullscreen quad here, because we compute the transparency on a fullscreen quad
        // we can optimize here the rendering speed by rendering quad covering just current view, and not whole canvas
        gl.viewport(0, 0, contentSize[0], contentSize[1]);

        FullScreenQuad.render(this.fullscreenVboID, this);

        let position = aView.getPosition();
        let size = aView.getSize();

        // set back the correct viewport based on the view position and size
        gl.viewport(position[0], position[1], size[0], size[1]);

        // unbind textures
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, null);

        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, null);

        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, null);

        this.unbindShader();
    }

    public render(aView: View, aOpaqueDepth, aColorAccBuffer, aDepthAccBuffer, aOutputBuffer) {
        let gl = this._context.getWebGLRenderingContext();

        // create opaque depth buffer
        this._renderGraph.getPasses().get(OpaqueDepth.TypeName).render(
            aView,
            aOpaqueDepth
        );

        // prepare for accumulation of depth complexity and colors
        gl.depthFunc(gl.ALWAYS);        // to keep depth writes

        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.enable(gl.BLEND);

        this._renderGraph.getPasses().get(DepthAccumulation.TypeName).render(
            aView,
            aOpaqueDepth,
            aDepthAccBuffer
        );

        this._renderGraph.getPasses().get(ColorAccumulation.TypeName).render(
            aView,
            aOpaqueDepth,
            aDepthAccBuffer,
            aColorAccBuffer
        );

        this._blendToOutputBuffer(aView, aOpaqueDepth, aColorAccBuffer, aDepthAccBuffer, aOutputBuffer);

        this._renderGraph.initGlStates();
    }
}

Factory.register(WeightedAverageTransparency);
